import {
  AccountBalance,
  Badge,
  BadgeOutlined,
  Business,
  Edit,
  Email,
  Engineering,
  GroupOutlined,
  Groups,
  LocationOn,
  People,
  Phone,
  PhotoCamera,
  WorkOutline,
  AllInclusive,
} from "@mui/icons-material";
import { Avatar, Box, Chip, Paper, Stack, Typography } from "@mui/material";

import { Backdrop, IconButton } from "../v3";

const iconMap = {
  Projects: <WorkOutline sx={{ fontSize: 16 }} />,
  Workers: <Engineering sx={{ fontSize: 16 }} />,
  Vendors: <Business sx={{ fontSize: 16 }} />,
  Subcontractors: <Groups sx={{ fontSize: 16 }} />,
  Clients: <People sx={{ fontSize: 16 }} />,
  Employees: <BadgeOutlined sx={{ fontSize: 16 }} />,
  Users: <GroupOutlined sx={{ fontSize: 16 }} />,
  Accounts: <AccountBalance sx={{ fontSize: 16 }} />,
};

export const CompanyDetails = ({
  avatarSrc = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0nMScgaGVpZ2h0PScxJyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnLz4=",
  name = "Company Name",
  phone = "",
  email = "",
  gstNumber = "",
  panNumber = "",
  location = "",
  isActive = true,
  onEdit = () => {},
  onHandleUpdateCompanyLogo = () => {},
  onActivateToggle = () => {},
  overviewItems = [],
  overviewLimits = {},
}) => {
  return (
    <Paper
      elevation={3}
      sx={{
        margin: "0px 11px 11px 11px",
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Cover Section */}
      <Box
        sx={{
          height: 100,
          backgroundColor: "#674BF5",
          position: "relative",
        }}
      >
        <Backdrop position={{ left: "20%", bottom: "65%" }} />
        <Backdrop position={{ right: "20%", bottom: "45%" }} />
        {/* Top Right Action Buttons */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: "absolute",
            top: 30,
            right: 30,
          }}
        >
          {/* Activate / Deactivate */}
          <IconButton
            buttonText={isActive ? "Deactivate" : "Activate"}
            tooltip={isActive ? "Deactivate Company" : "Activate Company"}
            onClick={onActivateToggle}
            styles={{
              fontSize: "12px",
              padding: "4px 10px",
            }}
            hoverStyles={{
              backgroundColor: isActive ? "#B71C1C" : "#2E7D32",
            }}
          />

          {/* Edit */}
          <IconButton icon={<Edit />} tooltip="Edit Company" onClick={onEdit} />
        </Stack>
      </Box>

      {/* Content Section */}
      <Box sx={{ px: 3, pb: 3, position: "relative" }}>
        {/* Avatar with camera overlay */}
        <Box sx={{ position: "absolute", top: -50 }}>
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <Avatar
              src={avatarSrc}
              sx={{
                width: 100,
                height: 100,
                backgroundColor: "#F2ECF9",
                border: "4px solid white",
                boxShadow: 2,
              }}
            />
            <IconButton
              icon={<PhotoCamera />}
              onClick={onHandleUpdateCompanyLogo}
              styles={{
                width: 32,
                height: 32,
                minWidth: 32,
                position: "absolute",
                bottom: 0,
                right: 0,
                boxShadow: 1,
                color: "#919191",
                backgroundColor: "#FFF",
              }}
              hoverStyles={{ backgroundColor: "#ecddfdff" }}
            />
          </Box>
        </Box>

        {/* Header Text */}
        <Box sx={{ pt: 7 }}>
          {/* Name + Status */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={600}>
              {name}
            </Typography>

            <Chip
              label={isActive ? "Active" : "Inactive"}
              size="small"
              sx={{
                fontWeight: 600,
                height: 22,
                backgroundColor: isActive ? "#E6F4EA" : "#FDECEA",
                color: isActive ? "#1B5E20" : "#B71C1C",
              }}
            />
          </Stack>

          {/* Contact Info */}
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {phone && (
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Phone sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {phone}
                </Typography>
              </Stack>
            )}

            {email && (
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Email sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {email}
                </Typography>
              </Stack>
            )}

            {gstNumber && (
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Badge sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {gstNumber}
                </Typography>
              </Stack>
            )}

            {panNumber && (
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Badge sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {panNumber}
                </Typography>
              </Stack>
            )}

            {location && (
              <Stack direction="row" spacing={1.2} alignItems="center">
                <LocationOn sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {location}
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Info Chips */}
          <Stack
            direction="row"
            sx={{
              mt: 2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {overviewItems.map((item, index) => {
              const limit = overviewLimits[item.label];
              const hasLimit = limit !== undefined && limit !== null;
              const atLimit = hasLimit && item.value >= limit;

              return (
                <Box key={index} sx={{ position: "relative", display: "inline-flex" }}>
                  <Chip
                    icon={iconMap[item.label]}
                    label={`${item.label}: ${item.value}`}
                    onClick={item.onClick}
                    clickable
                    sx={{
                      fontWeight: 600,
                      fontSize: "12px",
                      borderRadius: "6px",
                      backgroundColor: "#F2ECF9",
                      color: "#674BF5",
                      "& .MuiChip-icon": {
                        color: "#674BF5",
                        marginLeft: "6px",
                      },
                      "&:hover": { backgroundColor: "#e6dbfa" },
                    }}
                  />
                  {hasLimit ? (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        minWidth: 24,
                        height: 18,
                        borderRadius: "9px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 0.5,
                        fontSize: 10,
                        fontWeight: 700,
                        backgroundColor: atLimit ? "#FDECEA" : "#E6F4EA",
                        color: atLimit ? "#B71C1C" : "#1B5E20",
                        border: "1px solid",
                        borderColor: atLimit ? "#F5C6CB" : "#C8E6C9",
                        lineHeight: 1,
                        zIndex: 1,
                      }}
                    >
                      {item.value}/{limit}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#F5F5F5",
                        border: "1px solid #E0E0E0",
                        zIndex: 1,
                      }}
                    >
                      <AllInclusive sx={{ fontSize: 10, color: "#9E9E9E" }} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
};
